import { useReports } from './hooks/useReports';
import { exportToExcel } from './lib/excelExport';
import { useState } from 'react';

export default function BranchAnalytics() {
  const { branch } = useReports();
  const [filters, setFilters] = useState({
    period: 'monthly',
    branch: '',
    metric: 'revenue'
  });
  
  const query = branch(filters);
  
  if (query.isLoading) {
    return <div className="p-6">Loading...</div>;
  }
  
  const data = query.data?.data || [];
  const summary = query.data?.summary || {};
  const trends = query.data?.trends || [];
  
  const handleExport = () => {
    exportToExcel('Branch_Analytics_Report', [
      {
        name: 'Branch Performance',
        headers: ['Branch', 'Revenue', 'Orders', 'Customers', 'Growth', 'Performance'],
        data: data.map((d: any) => [
          d.branchName, d.revenue, d.orders, d.customers, d.growth + '%', d.performance + '%'
        ])
      },
      {
        name: 'Trends',
        headers: ['Period', 'Revenue', 'Orders', 'Growth'],
        data: trends.map((t: any) => [t.period, t.revenue, t.orders, t.growth + '%'])
      }
    ]);
  };
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-800">Branch Analytics</h1>
        <button className="btn-primary" onClick={handleExport}>
          <i className="ri-file-excel-2-line mr-2"></i>
          Export Analytics
        </button>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <select
              className="input"
              value={filters.period}
              onChange={e => setFilters({ ...filters, period: e.target.value })}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
            <select
              className="input"
              value={filters.branch}
              onChange={e => setFilters({ ...filters, branch: e.target.value })}
            >
              <option value="">All Branches</option>
              <option value="mumbai">Mumbai</option>
              <option value="delhi">Delhi</option>
              <option value="bangalore">Bangalore</option>
              <option value="chennai">Chennai</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metric</label>
            <select
              className="input"
              value={filters.metric}
              onChange={e => setFilters({ ...filters, metric: e.target.value })}
            >
              <option value="revenue">Revenue</option>
              <option value="orders">Orders</option>
              <option value="customers">Customers</option>
              <option value="growth">Growth</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Total Revenue</div>
          <div className="text-2xl font-bold text-[var(--color-primary)]">₹{(summary.totalRevenue || 0).toLocaleString()}</div>
          <div className="text-sm text-green-600 mt-1">
            <i className="ri-arrow-up-line"></i> +{summary.revenueGrowth || 0}%
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Total Orders</div>
          <div className="text-2xl font-bold text-green-600">{summary.totalOrders || 0}</div>
          <div className="text-sm text-green-600 mt-1">
            <i className="ri-arrow-up-line"></i> +{summary.ordersGrowth || 0}%
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Active Customers</div>
          <div className="text-2xl font-bold text-blue-600">{summary.activeCustomers || 0}</div>
          <div className="text-sm text-blue-600 mt-1">
            <i className="ri-arrow-up-line"></i> +{summary.customersGrowth || 0}%
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-sm text-gray-600">Avg Order Value</div>
          <div className="text-2xl font-bold text-purple-600">₹{(summary.avgOrderValue || 0).toLocaleString()}</div>
          <div className="text-sm text-purple-600 mt-1">
            <i className="ri-arrow-up-line"></i> +{summary.aovGrowth || 0}%
          </div>
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Performance Trends</h3>
          <div className="space-y-3">
            {trends.slice(0, 5).map((trend: any, index: number) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{trend.period}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">₹{trend.revenue?.toLocaleString()}</span>
                  <span className={`text-xs ${trend.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trend.growth >= 0 ? '+' : ''}{trend.growth}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Top Performing Branches</h3>
          <div className="space-y-3">
            {data.slice(0, 5).map((branch: any, index: number) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[var(--color-primary)]/10 rounded-full flex items-center justify-center">
                    <span className="text-[var(--color-primary)] font-semibold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <span className="font-medium">{branch.branchName}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">₹{branch.revenue?.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">{branch.performance}% performance</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/40">
              <tr>
                <th className="th">Branch</th>
                <th className="th">Revenue</th>
                <th className="th">Orders</th>
                <th className="th">Customers</th>
                <th className="th">Growth</th>
                <th className="th">Performance</th>
                <th className="th">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row: any, index: number) => (
                <tr key={index} className="tr">
                  <td className="td">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[var(--color-primary)]/10 rounded-full flex items-center justify-center">
                        <span className="text-[var(--color-primary)] font-semibold text-sm">
                          {row.branchName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium">{row.branchName}</div>
                        <div className="text-xs text-gray-500">{row.location}</div>
                      </div>
                    </div>
                  </td>
                  <td className="td">₹{row.revenue?.toLocaleString()}</td>
                  <td className="td">{row.orders}</td>
                  <td className="td">{row.customers}</td>
                  <td className="td">
                    <span className={`${row.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {row.growth >= 0 ? '+' : ''}{row.growth}%
                    </span>
                  </td>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[var(--color-primary)] h-2 rounded-full"
                          style={{ width: `${row.performance}%` }}
                        ></div>
                      </div>
                      <span className="text-sm">{row.performance}%</span>
                    </div>
                  </td>
                  <td className="td">
                    <span className={`badge ${row.performance >= 80 ? 'approved' : row.performance >= 60 ? 'pending' : 'rejected'}`}>
                      {row.performance >= 80 ? 'Excellent' : row.performance >= 60 ? 'Good' : 'Needs Improvement'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}